// Going Cold section — FE-036 / spec §7.0.8.
//
// Lists Accounts without recent Touchpoints or completed
// Commitments inside a configurable window. Tier 1 ships a 30/60/90
// day toggle; defaults to 60 (the BE-029 default).

import { ChevronRight, Snowflake } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import {
  buildAccountFilterUrl,
  buildCommitmentFilterUrl,
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCommonFilters,
  useGoingColdQuery,
  type GoingColdRow,
} from "@/features/dashboard";


export const GOING_COLD_SECTION = "going-cold" as const;


type DaysOption = 30 | 60 | 90;
const DAYS_OPTIONS: DaysOption[] = [30, 60, 90];
const DEFAULT_DAYS: DaysOption = 60;


export function GoingColdSection() {
  const filters = useCommonFilters();
  const [days, setDays] = useState<DaysOption>(DEFAULT_DAYS);
  // ACQ-008 — the header office / Account selection rides with the
  // widget's own window so the server narrows the list.
  const query = useGoingColdQuery({ ...filters, days });

  return (
    <DashboardCard
      section={GOING_COLD_SECTION}
      title="Going cold"
      subtitle="Accounts without recent activity."
      actions={<DaysSelector value={days} onChange={setDays} />}
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="list" rows={5} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load Going cold"
          retry={() => query.refetch()}
        />
      ) : query.data && query.data.rows.length > 0 ? (
        <ul
          data-testid="going-cold-rows"
          role="list"
          className="flex flex-col gap-2"
        >
          {query.data.rows.map((row) => (
            <GoingColdRowView key={row.account_id} row={row} />
          ))}
        </ul>
      ) : (
        <DashboardEmptyState
          title="No Accounts are going cold in this window"
          description={
            "Every active Account has a Touchpoint or completed Commitment " +
            `in the last ${days} days.`
          }
        />
      )}
    </DashboardCard>
  );
}


function GoingColdRowView({ row }: { row: GoingColdRow }) {
  const href = row.drill_through_url
    || buildAccountFilterUrl({ id: row.account_id });
  const days = row.days_since_activity;
  // Two sibling Links inside one row instead of a nested anchor: the
  // primary target is the Account drill-through, plus an optional
  // open-Commitments side link. Wrapping <a> inside <a> is invalid
  // HTML and produced a React Router warning under FE-036.
  return (
    <li>
      <div
        data-testid={`going-cold-row-${row.account_id}`}
        className="flex items-center gap-3 rounded-md bg-recessed px-3 py-2 transition-fast ease-out hover:bg-raised"
      >
        <span
          aria-hidden="true"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-raised text-text-tertiary"
        >
          <Snowflake size={14} />
        </span>
        <Link
          to={href}
          data-testid={`going-cold-row-link-${row.account_id}`}
          aria-label={
            `${row.legal_name}: ` +
            (days != null
              ? `${days} days since last activity, `
              : "no activity recorded yet, ") +
            `${row.open_commitment_count} open Commitment` +
            `${row.open_commitment_count === 1 ? "" : "s"}`
          }
          className="flex flex-1 items-center gap-3"
        >
          <span className="flex flex-1 flex-col">
            <span className="truncate text-sm font-medium text-text-primary">
              {row.legal_name}
            </span>
            <span className="text-xs text-text-tertiary">
              {formatActivityLine(row)}
            </span>
          </span>
          <ChevronRight
            size={14}
            className="text-text-tertiary"
            aria-hidden="true"
          />
        </Link>
        <CommitmentLink row={row} />
      </div>
    </li>
  );
}


function CommitmentLink({ row }: { row: GoingColdRow }) {
  if (row.open_commitment_count === 0) return null;
  return (
    <Link
      to={buildCommitmentFilterUrl({ client_id: row.account_id })}
      data-testid={`going-cold-commitments-${row.account_id}`}
      className="rounded-md px-2 py-0.5 text-xs font-medium text-teal hover:bg-raised"
    >
      {row.open_commitment_count} open
    </Link>
  );
}


function formatActivityLine(row: GoingColdRow): string {
  const parts: string[] = [];
  const days = row.days_since_activity;
  if (days != null) {
    parts.push(`${days} day${days === 1 ? "" : "s"} since last activity`);
  } else {
    parts.push("no recent activity");
  }
  if (row.last_touchpoint_at) {
    parts.push(`Touchpoint ${formatRelativeIso(row.last_touchpoint_at)}`);
  }
  if (row.last_completed_commitment_at) {
    parts.push(
      `Completed Commitment ${formatRelativeIso(row.last_completed_commitment_at)}`,
    );
  }
  return parts.join(" · ");
}


function formatRelativeIso(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
  });
}


function DaysSelector({
  value, onChange,
}: { value: DaysOption; onChange: (next: DaysOption) => void }) {
  return (
    <div
      data-testid="going-cold-days-selector"
      role="group"
      aria-label="Going cold window"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {DAYS_OPTIONS.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            aria-pressed={active}
            data-active={active ? "true" : "false"}
            data-testid={`going-cold-days-${opt}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt} d
          </button>
        );
      })}
    </div>
  );
}
